// src/components/Chatbot.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiArrowRight, FiStar } from "react-icons/fi";
import Image from "./Image";

// Chatbot Data
const chatbotData = {
  Orders: [
    { q: "How do I cancel an order?", a: "Go to 'Orders' > tap 'Cancel' before it's prepared." },
    { q: "Can I reorder the same meal?", a: "Yes, tap 'Reorder' from your order history." },
    { q: "Can I schedule an order?", a: "Yes! Choose 'Schedule for later' at checkout." },
    { q: "What is the status of my order?", a: "Check the 'Orders' tab for real-time updates." },
  ],
  Delivery: [
    { q: "Where’s my order?", a: "Track it live under the 'Orders' tab." },
    { q: "Is contactless delivery available?", a: "Yes, choose 'Leave at door' during checkout." },
    { q: "What if my order is late?", a: "Delays may happen due to traffic/weather. Contact support for assistance." },
    { q: "Can I change my delivery address?", a: "Yes, before the order is prepared, update it in the 'Orders' section." },
  ],
  Account: [
    { q: "Can I change my phone number?", a: "Yes, from your profile settings." },
    { q: "Can I update my delivery address?", a: "Yes, during checkout or in profile settings." },
    { q: "Do I need an account to order?", a: "No, you can use Guest Mode to order without signing up." },
    { q: "How do I reset my password?", a: "Go to 'Login' and click 'Forgot Password' to reset it." },
  ],
  Promotions: [
    { q: "How do I apply a promo code?", a: "Enter the code at checkout in the 'Promo Code' field." },
    { q: "Are there any current offers?", a: "Check the 'Offers' section for the latest deals!" },
  ],
};

const Chatbot = ({ darkMode, setToastMessage, isOpen, onClose }) => {
  const [chatHistory, setChatHistory] = useState(() => {
    const saved = sessionStorage.getItem("chatHistory");
    return saved
      ? JSON.parse(saved)
      : [{ type: "bot", text: "Hi! How can I assist you today?", timestamp: new Date().toISOString() }];
  });
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatSearch, setChatSearch] = useState("");
  const chatBodyRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem("chatHistory", JSON.stringify(chatHistory));
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    setChatHistory((prev) => [...prev, { type: "user", text: topic, timestamp: new Date().toISOString() }]);
  };

  const handleQuickReply = (q) => {
    const topic = Object.keys(chatbotData).find((key) => chatbotData[key].some((item) => item.q === q));
    if (topic) {
      const item = chatbotData[topic].find((i) => i.q === q);
      handleQuestionSelect(item.q, item.a);
    }
  };

  const handleQuestionSelect = (q, a) => {
    setChatHistory((prev) => [...prev, { type: "user", text: q, timestamp: new Date().toISOString() }]);
    setIsTyping(true);
    setTimeout(() => {
      setChatHistory((prev) => [
        ...prev,
        { type: "bot", text: a, timestamp: new Date().toISOString(), rating: null },
      ]);
      setIsTyping(false);
    }, 1000);
  };

  const handleChatSubmit = () => {
    if (!chatInput.trim()) return;
    const query = chatInput.toLowerCase();
    setChatHistory((prev) => [...prev, { type: "user", text: chatInput, timestamp: new Date().toISOString() }]);
    setChatInput("");
    const foundItem = Object.values(chatbotData)
      .flat()
      .find((item) => item.q.toLowerCase().includes(query));
    if (foundItem) {
      handleQuestionSelect(foundItem.q, foundItem.a);
    } else {
      setIsTyping(true);
      setTimeout(() => {
        setChatHistory((prev) => [
          ...prev,
          {
            type: "bot",
            text: "Sorry, I couldn't find an answer to that. Try selecting a topic or contacting live support!",
            timestamp: new Date().toISOString(),
            rating: null,
            suggestions: ["Where’s my order?", "How do I cancel an order?", "Are there any current offers?"],
          },
        ]);
        setIsTyping(false);
      }, 1000);
    }
  };

  const handleRateResponse = (index, rating) => {
    setChatHistory((prev) =>
      prev.map((entry, i) => (i === index && entry.type === "bot" ? { ...entry, rating } : entry))
    );
  };

  const resetChat = () => {
    setChatHistory([{ type: "bot", text: "Hi! How can I assist you today?", timestamp: new Date().toISOString() }]);
    setSelectedTopic(null);
    setChatSearch("");
    sessionStorage.removeItem("chatHistory");
    setToastMessage("Chat reset.");
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 100 }}
      className={`fixed bottom-20 right-6 w-[90vw] sm:w-96 max-h-[70vh] ${darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"} rounded-xl shadow-xl overflow-hidden flex flex-col z-[1000]`}
    >
      <div className="bg-green-500 text-white p-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Image src="/zoomo-mascot.png" alt="Zoomo Mascot" className="w-6 h-6 rounded-full" />
          <h2 className="font-bold text-sm">Zoomo Eats Help Centre</h2>
        </div>
        <button
          onClick={onClose}
          className="text-white text-lg font-semibold hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
          aria-label="Close chatbot"
        >
          <FiX />
        </button>
      </div>
      <div className={`p-3 border-b ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
        <input
          type="text"
          placeholder="Search past queries..."
          value={chatSearch}
          onChange={(e) => setChatSearch(e.target.value)}
          className={`w-full px-4 py-2 rounded-md text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-gray-100 text-gray-900 placeholder-gray-500 border-gray-300"} border focus:outline-none focus:ring-2 focus:ring-green-500`}
          aria-label="Search chat history"
        />
      </div>
      <div ref={chatBodyRef} className="p-4 space-y-3 flex-1 overflow-y-auto custom-scrollbar text-sm">
        {chatHistory
          .filter((entry) => (chatSearch ? entry.text.toLowerCase().includes(chatSearch.toLowerCase()) : true))
          .map((entry, index) => (
            <motion.div
              key={`chat-${index}`}
              initial={{ opacity: 0, x: entry.type === "user" ? 20 : -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`flex ${entry.type === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] px-3 py-2 rounded-lg shadow-md text-sm ${entry.type === "user" ? "bg-green-500 text-white" : darkMode ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-900"}`}
              >
                {entry.type === "bot" && (
                  <span className="block text-[10px] font-semibold mb-0.5 text-green-500">Zoomo Bot</span>
                )}
                <p>{entry.text}</p>
                <span className="block text-[10px] text-gray-400 mt-1">
                  {new Date(entry.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
                {entry.type === "bot" && (
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-1">
                      <span className="text-[10px]">Rate this response:</span>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={`star-${star}-${index}`}
                          onClick={() => handleRateResponse(index, star)}
                          className={`text-[10px] ${entry.rating && star <= entry.rating ? "text-yellow-500" : "text-gray-400"} hover:text-yellow-500 focus:outline-none`}
                          aria-label={`Rate response ${star} stars`}
                        >
                          <FiStar />
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => handleQuickReply(entry.text)}
                      className={`text-[10px] px-3 py-1 rounded-md ${darkMode ? "bg-gray-600 text-white hover:bg-gray-500" : "bg-white text-gray-900 hover:bg-gray-100"} transition-all`}
                      aria-label="Ask again"
                    >
                      Ask Again
                    </button>
                    {entry.suggestions && (
                      <div className="mt-2 space-y-1">
                        <p className="text-[10px] font-semibold">Suggested Questions:</p>
                        {entry.suggestions.map((suggestion, i) => (
                          <button
                            key={`suggestion-${i}`}
                            onClick={() => handleQuickReply(suggestion)}
                            className={`block w-full text-left text-[10px] px-3 py-1 rounded-md ${darkMode ? "bg-gray-600 text-white hover:bg-gray-500" : "bg-white text-gray-900 hover:bg-gray-100"} transition-all`}
                            aria-label={`Ask ${suggestion}`}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className={`px-3 py-2 rounded-lg shadow-md text-sm flex gap-1 items-center ${darkMode ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-900"}`}>
              <span className="typing-dot animate-bounce delay-0">.</span>
              <span className="typing-dot animate-bounce delay-150">.</span>
              <span className="typing-dot animate-bounce delay-300">.</span>
            </div>
          </div>
        )}
        {!selectedTopic && !isTyping && (
          <div className="grid grid-cols-2 gap-2">
            {Object.keys(chatbotData).map((topic) => (
              <motion.button
                key={`topic-${topic}`}
                onClick={() => handleTopicSelect(topic)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`px-3 py-2 rounded-md shadow-sm text-sm w-full transition-all ${darkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-white text-gray-900 hover:bg-gray-100"}`}
                aria-label={`Select ${topic} topic`}
              >
                {topic}
              </motion.button>
            ))}
            <motion.button
              onClick={() => window.open("https://wa.me/919070055040", "_blank")}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`px-3 py-2 rounded-md shadow-sm text-sm w-full transition-all ${darkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-white text-gray-900 hover:bg-gray-100"}`}
              aria-label="Open live chat support"
            >
              Live Chat
            </motion.button>
          </div>
        )}
        {selectedTopic && !isTyping && (
          <div className="space-y-2">
            {chatbotData[selectedTopic].map((item, i) => (
              <motion.button
                key={`question-${i}`}
                onClick={() => handleQuestionSelect(item.q, item.a)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full text-left rounded-md px-3 py-2 shadow-sm transition-all text-sm ${darkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-white text-gray-900 hover:bg-gray-100"}`}
                aria-label={`Ask ${item.q}`}
              >
                {item.q}
              </motion.button>
            ))}
            <button
              onClick={resetChat}
              className="w-full text-center text-green-500 hover:text-green-600 text-sm"
              aria-label="Return to topic selection"
            >
              Back to Topics
            </button>
          </div>
        )}
      </div>
      <div className={`p-3 border-t ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Type your question..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleChatSubmit()}
            className={`flex-1 px-4 py-2 rounded-md text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-gray-100 text-gray-900 placeholder-gray-500 border-gray-300"} border focus:outline-none focus:ring-2 focus:ring-green-500`}
            aria-label="Type a question for the chatbot"
          />
          <motion.button
            onClick={handleChatSubmit}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md text-sm shadow-sm hover:shadow-md transition-all"
            aria-label="Send chatbot message"
          >
            <FiArrowRight />
          </motion.button>
        </div>
        <div className="mt-2 flex justify-between gap-2">
          <motion.button
            onClick={resetChat}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`text-sm px-3 py-1 rounded-md ${darkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-white text-gray-900 hover:bg-gray-100"} transition-all`}
            aria-label="Clear chat history"
          >
            Clear Chat
          </motion.button>
          <motion.button
            onClick={() => window.open("https://wa.me/919070055040", "_blank")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`text-sm px-3 py-1 rounded-md ${darkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-white text-gray-900 hover:bg-gray-100"} transition-all`}
            aria-label="Contact live support"
          >
            Live Support
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default Chatbot;